import React, { useState, useEffect } from 'react';

function PackageScanner({ selectedDeliveries, onComplete, onCancel }) {
  const [scannedPackages, setScannedPackages] = useState([]);
  const [currentInput, setCurrentInput] = useState('');
  const [error, setError] = useState('');
  const [scanMode, setScanMode] = useState('qr'); // 'manual' or 'qr'
  const [currentPackageIndex, setCurrentPackageIndex] = useState(0);

  useEffect(() => {
    // Auto-focus input when component mounts in manual mode
    if (scanMode === 'manual') {
      const input = document.getElementById('package-input');
      if (input) input.focus();
    }
  }, [scanMode]);

  const handleScan = (code) => {
    setError('');
    
    // Check if code matches any of the selected deliveries
    const delivery = selectedDeliveries.find(d => 
      d.packageCode === code || d.packageCode === code.toUpperCase()
    );

    if (!delivery) {
      setError(`Package code "${code}" not found in selected deliveries`);
      return;
    }

    // Check if already scanned
    if (scannedPackages.some(p => p.id === delivery.id)) {
      setError(`Package ${delivery.packageCode} already scanned`);
      return;
    }

    // Add to scanned packages
    setScannedPackages([...scannedPackages, delivery]);
    setCurrentInput('');

    // If all packages scanned, auto-complete
    if (scannedPackages.length + 1 === selectedDeliveries.length) {
      setTimeout(() => {
        onComplete([...scannedPackages, delivery]);
      }, 500);
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (currentInput.trim()) {
      handleScan(currentInput.trim());
    }
  };

  const handleRemoveScanned = (id) => {
    setScannedPackages(scannedPackages.filter(p => p.id !== id));
    // Move back to previous package if current one is removed
    if (currentPackageIndex > 0) {
      setCurrentPackageIndex(currentPackageIndex - 1);
    }
  };

  const handleWarehouseScan = (packageId) => {
    // Simulates warehouse scanning the QR code
    const delivery = selectedDeliveries.find(d => d.id === packageId);
    if (!delivery) return;

    // Check if already scanned
    if (scannedPackages.some(p => p.id === delivery.id)) {
      return;
    }

    // Add to scanned packages
    const newScanned = [...scannedPackages, delivery];
    setScannedPackages(newScanned);

    // Move to next package
    if (currentPackageIndex < selectedDeliveries.length - 1) {
      setCurrentPackageIndex(currentPackageIndex + 1);
    }

    // If all packages scanned, auto-complete after a short delay
    if (newScanned.length === selectedDeliveries.length) {
      setTimeout(() => {
        onComplete(newScanned);
      }, 1000);
    }
  };

  const handleComplete = () => {
    if (scannedPackages.length === 0) {
      setError('Please have the warehouse scan at least one package');
      return;
    }

    if (scannedPackages.length < selectedDeliveries.length) {
      const confirm = window.confirm(
        `Only ${scannedPackages.length} of ${selectedDeliveries.length} packages scanned. Continue anyway?`
      );
      if (!confirm) return;
    }

    onComplete(scannedPackages);
  };

  const unscannedPackages = selectedDeliveries.filter(
    d => !scannedPackages.some(p => p.id === d.id)
  );

  return (
    <div className="package-scanner">
      <div className="scanner-header">
        <h2>Scan Packages</h2>
        <button className="btn-close" onClick={onCancel}>✕</button>
      </div>

      <div className="scanner-progress">
        <div className="progress-text">
          {scannedPackages.length} of {selectedDeliveries.length} packages scanned
        </div>
        <div className="progress-bar">
          <div 
            className="progress-fill"
            style={{ width: `${(scannedPackages.length / selectedDeliveries.length) * 100}%` }}
          ></div>
        </div>
      </div>

      {/* Scan Mode Toggle */}
      <div className="scan-mode-toggle">
        <button
          className={`mode-btn ${scanMode === 'manual' ? 'active' : ''}`}
          onClick={() => setScanMode('manual')}
        >
          ⌨️ Manual Entry
        </button>
        <button
          className={`mode-btn ${scanMode === 'qr' ? 'active' : ''}`}
          onClick={() => setScanMode('qr')}
        >
          📷 QR Scanner
        </button>
      </div>

      {/* Scanner Input */}
      <div className="scanner-input-section">
        {scanMode === 'manual' ? (
          <form onSubmit={handleManualSubmit} className="manual-input-form">
            <div className="input-group">
              <input
                id="package-input"
                type="text"
                value={currentInput}
                onChange={(e) => setCurrentInput(e.target.value.toUpperCase())}
                placeholder="Enter package code (e.g., PKG1001)"
                className="package-input"
                autoComplete="off"
              />
              <button type="submit" className="btn-scan">
                Verify
              </button>
            </div>
          </form>
        ) : (
          <div className="qr-display-section">
            <div className="qr-instruction">
              <h3>📱 Show this to warehouse staff</h3>
              <p>Present your phone to the warehouse scanner</p>
            </div>

            {unscannedPackages.length > 0 ? (
              <div className="qr-code-display">
                {unscannedPackages.map((pkg, index) => (
                  <div key={pkg.id} className={`qr-package-card ${index === 0 ? 'active' : ''}`}>
                    <div className="qr-package-header">
                      <div className="package-number">Package {scannedPackages.length + index + 1} of {selectedDeliveries.length}</div>
                      <div className="package-id-large">{pkg.packageCode}</div>
                    </div>
                    
                    {/* QR Code Display */}
                    <div className="qr-code-container">
                      <div className="qr-code-box">
                        {/* This is a visual representation - in production, use a QR library */}
                        <svg viewBox="0 0 100 100" className="qr-code-svg">
                          {/* QR Code pattern - simplified representation */}
                          <rect width="100" height="100" fill="white"/>
                          <rect x="10" y="10" width="15" height="15" fill="black"/>
                          <rect x="75" y="10" width="15" height="15" fill="black"/>
                          <rect x="10" y="75" width="15" height="15" fill="black"/>
                          
                          {/* Data pattern based on package code */}
                          {pkg.packageCode.split('').map((char, i) => (
                            <rect 
                              key={i}
                              x={30 + (i % 5) * 8} 
                              y={30 + Math.floor(i / 5) * 8} 
                              width="6" 
                              height="6" 
                              fill={char.charCodeAt(0) % 2 === 0 ? 'black' : 'white'}
                            />
                          ))}
                          
                          <text x="50" y="95" textAnchor="middle" fontSize="8" fill="black">
                            {pkg.packageCode}
                          </text>
                        </svg>
                      </div>
                      
                      <div className="qr-code-info">
                        <div className="qr-data-item">
                          <strong>Order:</strong> {pkg.orderId}
                        </div>
                        <div className="qr-data-item">
                          <strong>Destination:</strong> {pkg.deliveryAddress}
                        </div>
                      </div>
                    </div>

                    {/* Demo scan button */}
                    <button 
                      className="btn-demo-scan"
                      onClick={() => handleWarehouseScan(pkg.id)}
                    >
                      ✓ Simulate Warehouse Scan
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="all-scanned-message">
                <div className="success-icon">✅</div>
                <h3>All Packages Scanned!</h3>
                <p>Ready to complete pickup</p>
              </div>
            )}

            <button className="btn-secondary" onClick={() => setScanMode('manual')}>
              Switch to Manual Entry
            </button>
          </div>
        )}

        {error && (
          <div className="error-message scanner-error">{error}</div>
        )}
      </div>

      {/* Unscanned Packages */}
      {unscannedPackages.length > 0 && (
        <div className="unscanned-section">
          <h3>Packages to Scan</h3>
          <div className="packages-list">
            {unscannedPackages.map(pkg => (
              <div key={pkg.id} className="package-item unscanned">
                <div className="package-icon">📦</div>
                <div className="package-info">
                  <div className="package-code">{pkg.packageCode}</div>
                  <div className="package-order">{pkg.orderId}</div>
                </div>
                <button
                  className="btn-quick-scan"
                  onClick={() => handleScan(pkg.packageCode)}
                >
                  Quick Scan
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Scanned Packages */}
      {scannedPackages.length > 0 && (
        <div className="scanned-section">
          <h3>Scanned Packages ✓</h3>
          <div className="packages-list">
            {scannedPackages.map(pkg => (
              <div key={pkg.id} className="package-item scanned">
                <div className="package-icon">✅</div>
                <div className="package-info">
                  <div className="package-code">{pkg.packageCode}</div>
                  <div className="package-order">{pkg.orderId}</div>
                  <div className="package-address">{pkg.deliveryAddress}</div>
                </div>
                <button
                  className="btn-remove"
                  onClick={() => handleRemoveScanned(pkg.id)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="scanner-actions">
        <button className="btn-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button 
          className="btn-complete"
          onClick={handleComplete}
          disabled={scannedPackages.length === 0}
        >
          Complete Pickup ({scannedPackages.length})
        </button>
      </div>
    </div>
  );
}

export default PackageScanner;
